import { useCallback, useEffect, useMemo, useState } from 'react';
import { Application } from '@core/Application';
import { Organization } from '@models/Organization';
import { SharedApplication } from '@shared/core/SharedApplication';
import type { LocalStorageInterface } from '@shared/core/LocalStorageInterface';

const organizationStorageKey = 'qontact.organizationId';
const organizationEventName = 'qontact:organizationId';
const defaultTimezone = 'America/Argentina/Buenos_Aires';

const dispatchOrganizationChanged = () => {
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event(organizationEventName));
    }
};

export const useSelectedOrganization = (
    orgs: Organization[],
    localStorage: LocalStorageInterface = SharedApplication.shared().localStorageController,
    application: Application = Application.shared(),
    sharedApplication: SharedApplication = SharedApplication.shared(),
) => {
    const readStoredOrganizationId = useCallback(() => {
        return localStorage.getItem(organizationStorageKey) ?? undefined;
    }, [localStorage]);

    const [selectedOrganizationId, setSelectedOrganizationId] = useState<string | undefined>(() => {
        return readStoredOrganizationId();
    });

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const handleOrganizationChanged = () => {
            setSelectedOrganizationId(readStoredOrganizationId());
        };

        window.addEventListener(organizationEventName, handleOrganizationChanged);
        return () => {
            window.removeEventListener(organizationEventName, handleOrganizationChanged);
        };
    }, [readStoredOrganizationId]);

    const selectedOrganization = useMemo(() => {
        if (!selectedOrganizationId) {
            return undefined;
        }

        return orgs.find((organization) => organization.id === selectedOrganizationId);
    }, [orgs, selectedOrganizationId]);

    const setSelectedOrganization = useCallback((organization?: Organization) => {
        const nextOrganizationId = organization?.id;
        const storedOrganizationId = readStoredOrganizationId();

        if (!nextOrganizationId) {
            if (storedOrganizationId) {
                localStorage.removeItem(organizationStorageKey);
                dispatchOrganizationChanged();
            }
            setSelectedOrganizationId(undefined);
            return;
        }

        if (storedOrganizationId !== nextOrganizationId) {
            localStorage.setItem(organizationStorageKey, nextOrganizationId);
            dispatchOrganizationChanged();
        }

        setSelectedOrganizationId(nextOrganizationId);
    }, [localStorage, readStoredOrganizationId]);

    useEffect(() => {
        if (orgs.length === 0) {
            setSelectedOrganizationId(undefined);
            return;
        }

        const storedOrganizationId = readStoredOrganizationId();
        const storedOrganization = orgs.find((organization) => organization.id === storedOrganizationId);

        if (storedOrganization) {
            if (selectedOrganizationId !== storedOrganization.id) {
                setSelectedOrganizationId(storedOrganization.id);
            }
            return;
        }

        setSelectedOrganization(orgs[0]);
    }, [orgs, readStoredOrganizationId, selectedOrganizationId, setSelectedOrganization]);

    useEffect(() => {
        application.currentOrganizationId = selectedOrganization?.id ?? null;
        application.timezone = selectedOrganization?.timezone ?? defaultTimezone;
        sharedApplication.organizationID = selectedOrganization?.id ?? '';
    }, [application, selectedOrganization, sharedApplication]);

    return {
        selectedOrganization,
        selectedOrganizationId,
        setSelectedOrganization,
    };
};
